import { CommonModule } from '@angular/common';
import { Component, inject, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { FilterMatchMode, FilterMetadata, FilterService, MenuItem, MessageService, SelectItem } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RatingModule } from 'primeng/rating';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { TreeSelectModule } from 'primeng/treeselect';

import { firstValueFrom, forkJoin } from 'rxjs';
import { SETTINGS, Settings } from '../../../../app.settings';
import { ProcessingComponent } from '../../../../components';
import { TooltipModule } from 'primeng/tooltip';
import {
  FeedbackService,
  IAppUser,
  IFeedbackAnswer,
  IFeedbackAnswerRequest,
  ISearchRequest,
  ISearchSort,
  ISearchRequestFilter,
  ISearchFilterValue,
  ISurveyQuestion
} from '../../../../services/insecta';

import { afterRender } from '../../../../common/after-render';
import { notifyAndTrackError } from '../../../../common/error';
import { clone, getItemDescriptionColumn, isNotEmpty, isStringEmpty, isStringEqual, toShortDate } from '../../../../common/functions';
import { questionSort } from '../../../../common/sorts';
import { aggregateValidation, VALIDATION_PREFIX } from '../../../../common/validation';
import { AppInitService, IInsectaPermissions } from '../../../../services/app-init.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { MSGraphService } from '../../../../services/msgraph.service';
import { RequesterDashboardService } from '../../../../services/insecta/dashboard/requester-dashboard.service';

export interface IQuestion {
  question: ISurveyQuestion;
  answer: IFeedbackAnswer;
}

@Component({
  selector: 'app-requester-requester-receive',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CheckboxModule,
    FileUploadModule,
    FloatLabelModule,
    FormsModule,
    ImageModule,
    InputTextModule,
    TableModule,
    TextareaModule,
    TreeSelectModule,
    RouterModule,
    ProcessingComponent,
    PaginatorModule,
    RadioButtonModule,
    RatingModule,
    InputNumberModule,
    DialogModule,
    Breadcrumb,
    TooltipModule
  ],
  templateUrl: './requester-receive.html'
})
export class RequesterReceive implements OnInit {
  @Input() appUser!: IAppUser;
  @ViewChild('dt') dt!: Table;

  permissions!: IInsectaPermissions;
  userSettingsJson: { [key: string]: any } = {};
  route: any;
  cols: any[] = [];
  dlgVars: any = {
    expanded: false,
    isVisible: false
  };
  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    pageId: `Dashboard:Requester:Receive`,
    breadcrumbItems: <MenuItem[]>[]
  };
  columns: string[] = [
    'OrderId',
    'InstanceId',
    'InstanceSequence',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'DeliveryDate',
    'DeliveryLocationName',
    'InstanceQuantity',
    'InsectSetupDate',
    'SubstrateSetupDate',
    'VendorName',
    'InstanceNote',
    'RequesterName'
  ];

  instances: any[] = [];
  feedback: IQuestion[] = [];
  selectAllToggle: boolean = false;
  selectedInstances: any[] = [];
  isFeedbackDialogValid: boolean = false;

  skip: number = 0;
  take: number = 10;
  totalCount: number = 0;

  activeColumnFilters: Set<string> = new Set<string>();
  filtersActive: boolean = false;

  customMatchModeOptions: SelectItem[] = [
    { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
    { label: 'Contains', value: FilterMatchMode.CONTAINS },
    { label: 'Not Contains', value: FilterMatchMode.NOT_CONTAINS },
    { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
    { label: 'Equals', value: FilterMatchMode.EQUALS },
    { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];
  dateMatchModeOptions: SelectItem[] = [
    { label: 'Date Is', value: FilterMatchMode.DATE_IS },
    { label: 'Date Is Not', value: FilterMatchMode.DATE_IS_NOT },
    { label: 'Date Before', value: FilterMatchMode.DATE_BEFORE },
    { label: 'Date After', value: FilterMatchMode.DATE_AFTER },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];

  numericMatchModeOptions: SelectItem[] = [
    { label: 'Equals', value: FilterMatchMode.EQUALS },
    { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
    { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
    { label: 'Less Than or Equal', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
    { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
    { label: 'Greater Than or Equal', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];
  requesterDashboardService: RequesterDashboardService = inject(RequesterDashboardService);
  searchRequest: ISearchRequest = {
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };

  siteName: string = '';
  itemDescName: string = 'ItemDescription';

  constructor(
    @Inject(SETTINGS) private settings: Settings,
    private appInit: AppInitService,
    private appInsights: AppInsightsService,
    private feedbackService: FeedbackService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private filterService: FilterService
  ) {
    this.pageVars.pageId = `${settings.environment}:Dashboard:RequesterDashboard:Receive`;
    appInsights.trackPageView(this.pageVars.pageId, `/dashboard/requesterdashboard/receive`);
  }

  ngOnInit(): void {
    this.pageVars.isReady = false;
    this.appUser = this.appInit.appUser;

    this.filterService.register('isEmpty', (value: any): boolean => {
      return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    });

    this.filterService.register('isNotEmpty', (value: any): boolean => {
      return value !== undefined && value !== null && typeof value === 'string' && value.trim() !== '';
    });

    if (this.appUser) {
      this.userSettingsJson =
        !isStringEmpty(this.appUser.userSettingsJson) || isStringEqual(this.appUser.userSettingsJson, '{}')
          ? JSON.parse(this.appUser.userSettingsJson)
          : {};

      this.siteName = this.userSettingsJson['Default Site'] || '';
      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
      this.initializeColumns();
    }

    this.permissions = this.appInit.permissions;
    if (this.permissions.canViewInsectaryDashboard && this.permissions.canViewRequestorDashboard) {
      this.pageVars.breadcrumbItems = [{ label: 'Requester Dashboard', routerLink: '/requesterdashboard' }, { label: 'Receive Orders' }];
    } else {
      this.pageVars.breadcrumbItems = [{ label: 'Dashboard', routerLink: '/requesterdashboard' }, { label: 'Receive Orders' }];
    }

    const request = {
      profile: this.graphService.getMyProfile$(),
      questions: this.feedbackService.getQuestions()
    };
    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.pageVars.profile = response.profile;
        this.pageVars.currentUser = response.profile.mail;

        this.feedback = response.questions
          .sort((a, b) => questionSort(a, b))
          .map((q) => {
            const question: any = <IQuestion>{
              question: <ISurveyQuestion>q,
              answer: <IFeedbackAnswer>{
                answerText: undefined,
                questionId: q.id
              }
            };
            return question;
          });
        if (this.dt) {
          this.dt.onLazyLoad.emit(this.dt.createLazyLoadMetadata());
        } else {
          afterRender(() => this.loadInitialData());
        }
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterReceive', action: 'InitLoadProfileQuestions' });
        this.pageVars.isReady = true;
      });
  }

  initializeColumns() {
    this.cols = [
      { field: 'InstanceSequence', header: 'Order Item Instance ID', type: 'text', template: 'link' },
      { field: 'DeliveryDate', header: 'Delivery Date', type: 'date' },
      { field: 'DeliveryLocationName', header: 'Delivery Location', type: 'text' },
      { field: this.itemDescName, header: 'Item Description', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'InstanceQuantity', header: 'Quantity', type: 'numeric' },
      { field: 'InsectSetupDate', header: 'Insect Prep Date', type: 'date' },
      { field: 'SubstrateSetupDate', header: 'Media Prep Date', type: 'date' },
      { field: 'VendorName', header: 'Vendor', type: 'text' },
      { field: 'InstanceNote', header: 'Note', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } }
    ];

    this.syncColumnState();
  }

  private syncColumnState() {
    const stateKey = 'receive-order-table';
    try {
      const storedState = localStorage.getItem(stateKey);
      if (!storedState) return;

      const state = JSON.parse(storedState);
      const columnOrder = state?.columnOrder as string[] | undefined;

      if (columnOrder && columnOrder.length !== this.cols.length) {
        delete state.columnOrder;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage column order', e);
    }
  }

  loadInitialData() {
    this.searchRequest.filters = [];
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: `SiteName`,
      logic: `and`,
      values: [<ISearchFilterValue>{ operator: `equals`, value: this.siteName }]
    });
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: `RequesterEmail`,
      logic: `and`,
      values: [<ISearchFilterValue>{ operator: `equals`, value: this.pageVars.currentUser }]
    });

    this.searchRequest.sorts = [];
    this.searchRequest.sorts.push(<ISearchSort>{ name: 'DeliveryDate', direction: 'ASC' });

    this.executeSearch();
  }

  executeSearch() {
    const request = this.searchRequest;
    request.columns = [...this.columns.filter((x) => !x.startsWith('ItemDescription')), this.itemDescName];

    firstValueFrom(this.requesterDashboardService.getReceiveTable(request))
      .then(async (response) => {
        this.totalCount = response?.totalCount || 0;
        this.instances = JSON.parse(response?.results ?? `[]`);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterReceive', action: 'ExecuteSearch' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }
  private isClearing: boolean = false;
  handleTableEvents(event: TableLazyLoadEvent) {
    if (this.isClearing) return;
    if (!this.pageVars.currentUser) return;
    this.pageVars.isReady = false;
    this.activeColumnFilters.clear();

    this.searchRequest.filters = [];
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: `SiteName`,
      logic: `and`,
      values: [<ISearchFilterValue>{ operator: `equals`, value: this.siteName }]
    });
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: `RequesterEmail`,
      logic: `and`,
      values: [<ISearchFilterValue>{ operator: `equals`, value: this.pageVars.currentUser }]
    });

    this.columns.forEach((columnName) => {
      const columnFilters = event.filters![columnName] as FilterMetadata[];
      if (columnFilters && columnFilters.length > 0) {
        const tableFilter = <ISearchRequestFilter>{
          fieldname: columnName,
          logic: columnFilters[0].operator,
          values: []
        };
        columnFilters.forEach((filterValue) => {
          if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode.toUpperCase(),
              value: ''
            });
          } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode || `contains`,
              value: filterValue.matchMode?.startsWith(`date`) ? toShortDate(filterValue.value) : `${filterValue.value}`
            });
          }
        });
        if (tableFilter.values.length > 0) {
          this.searchRequest.filters.push(tableFilter);
          this.activeColumnFilters.add(columnName);
        }
      }
    });

    this.searchRequest.sorts = [];
    if (isNotEmpty(event.sortField)) {
      this.searchRequest.sorts.push(<ISearchSort>{ name: event.sortField, direction: event.sortOrder === 1 ? 'ASC' : 'DESC' });
    } else {
      this.searchRequest.sorts.push(<ISearchSort>{ name: 'DeliveryDate', direction: 'ASC' });
    }

    this.searchRequest.skip = event.first;
    this.searchRequest.take = event.rows ?? this.take;
    this.filtersActive = this.activeColumnFilters.size > 0 || isNotEmpty(event.sortField);

    this.executeSearch();
  }

  getColumnHeaderStyle(columnName: string): any {
    return this.activeColumnFilters.has(columnName) ? { 'background-color': '#e0f7fa', 'border-bottom': '2px solid #006064' } : {};
  }

  clearFilters(table: Table) {
    if (this.isClearing) return;
    table.sortField = null;
    table.sortOrder = 1;

    if (table.filters) {
      Object.keys(table.filters).forEach((key) => {
        const col = this.cols?.find((c: any) => c.field === key);
        const filters = table.filters[key] as any[];
        if (Array.isArray(filters)) {
          filters.forEach((f) => {
            if (f.matchMode === 'isEmpty' || f.matchMode === 'isNotEmpty') {
              if (col?.type === 'date') {
                f.matchMode = 'dateIs';
              } else if (col?.type === 'numeric') {
                f.matchMode = 'equals';
              } else {
                f.matchMode = 'contains';
              }
              f.value = null;
            }
          });
        }
      });
    }

    table.clear();
    this.activeColumnFilters.clear();
    this.filtersActive = false;

    try {
      const stateKey = 'receive-order-table';
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        const state = JSON.parse(storedState);
        delete state.sortField;
        delete state.sortOrder;
        delete state.filters;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage in clearFilters', e);
    }

    setTimeout(() => {
      this.searchRequest.filters = [];
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: `SiteName`,
        logic: `and`,
        values: [<ISearchFilterValue>{ operator: `equals`, value: this.siteName }]
      });
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: `RequesterEmail`,
        logic: `and`,
        values: [<ISearchFilterValue>{ operator: `equals`, value: this.pageVars.currentUser }]
      });
      this.searchRequest.sorts = [{ name: 'DeliveryDate', direction: 'ASC' }];
      this.searchRequest.skip = 0;
      this.searchRequest.take = this.take;
      this.isClearing = false;
      this.executeSearch();
    }, 100);
  }
  resetPage(table: Table) {
    table.clear();
    table.clearState();
    window.location.reload();
  }

  toggleSelectAll(checkAll: boolean) {
    if (!checkAll) {
      this.selectedInstances = [];
      this.selectAllToggle = false;
      this.computeFeedbackDialogValidation(false);
      return;
    }

    this.pageVars.isReady = false;

    const selectAllSearch = this.searchRequest;
    selectAllSearch.skip = 0;
    selectAllSearch.take = this.totalCount;

    firstValueFrom(this.requesterDashboardService.getReceiveTable(selectAllSearch))
      .then((response) => {
        const results = JSON.parse(response?.results ?? `[]`);
        this.selectedInstances = results.map((x: any) => x.InstanceId);
        this.selectAllToggle = true;
        this.computeFeedbackDialogValidation(false);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterReceive', action: 'SelectAll' });
        this.selectedInstances = [];
        this.selectAllToggle = false;
        this.computeFeedbackDialogValidation(false);
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  selectRowClick() {
    this.selectAllToggle = this.selectedInstances.length == this.totalCount;
    this.computeFeedbackDialogValidation(false);
  }

  async export() {
    this.pageVars.isReady = false;

    try {
      const exportRequest = this.searchRequest;
      exportRequest.columns = [...this.columns.filter((x) => !x.startsWith('ItemDescription')), this.itemDescName];
      exportRequest.skip = 0;
      exportRequest.take = this.totalCount;
      await this.requesterDashboardService.getReceiveExport(exportRequest);
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterReceive', action: 'Export' });
    } finally {
      this.pageVars.isReady = true;
    }
  }

  dlgCancel() {
    this.dlgReset();
    this.dlgVars.isVisible = false;
    this.dlgVars.expanded = false;
    this.computeFeedbackDialogValidation(false);
  }

  dlgReset() {
    this.feedback = this.feedback.map((x) => {
      const y: IQuestion = clone(x);
      y.answer.answerText = undefined;
      return y;
    });
  }

  dlgSave() {
    if (!this.computeFeedbackDialogValidation(true)) {
      return;
    }
    this.pageVars.isReady = false;

    const feedbackRequest = <IFeedbackAnswerRequest>{
      entityType: `OrderItemInstance`,
      entityIds: this.selectedInstances,
      answers: this.feedback
        .filter((x) => isNotEmpty(x.answer.answerText))
        .map((x) => <IFeedbackAnswer>{ answerText: `${x.answer.answerText}`, questionId: x.answer.questionId })
    };

    firstValueFrom(this.feedbackService.postFeedback(feedbackRequest))
      .then(() => {
        this.dlgReset();
        this.selectedInstances = [];
        this.selectAllToggle = false;
        this.dlgVars.isVisible = false;
        this.dlgVars.expanded = false;
        this.executeSearch();
        this.computeFeedbackDialogValidation(false);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterReceive', action: 'SaveFeedback' });
        this.pageVars.isReady = true;
      });
  }

  dlgShow() {
    this.dlgReset();
    this.pageVars.isReady = false;
    this.dlgVars.isVisible = true;
    this.pageVars.isReady = true;
    this.computeFeedbackDialogValidation(false);
  }

  private computeFeedbackDialogValidation(showToast: boolean) {
    this.isFeedbackDialogValid = aggregateValidation(
      this.msgService,
      [
        {
          valid: isStringEmpty(this.feedback.find((x) => x.question.order === 1)?.answer.answerText ?? undefined) === false,
          message: 'The first feedback question is required.'
        }
      ],
      { prefix: VALIDATION_PREFIX, silent: !showToast }
    );
    return this.isFeedbackDialogValid;
  }

  onFeedbackAnswerChange() {
    this.computeFeedbackDialogValidation(false);
  }
}
